import { Wrench } from "lucide-react";

import { StaffEmailToggle } from "@/components/layout/StaffEmailToggle";
import { detectEnvironment } from "@/lib/environment";
import type {
  ModuleDefinition,
  ModuleSection,
  ModuleSettingsToggle,
} from "@/lib/module-registry";
import { APP_ROUTES, type AppRoute } from "@/lib/routes";

/**
 * Operations — the back-office module, in three groups: Directory (Payees,
 * plus the Suppliers and Utilities records), Pricing (DR & Peak Shaving,
 * Matrix Pricing, SES Calculators) and Workflow (Cases, Tasks).
 *
 * Split out of the former "Sales" module (now Energy Advisor). These pages
 * are maintained by ops rather than worked in by an advisor: an advisor
 * *consumes* matrix pricing and the scoring rubric, but doesn't tune them.
 * Carrying both in one module meant one sidebar mixing daily advisor work
 * with occasional back-office configuration.
 *
 * Its route entries still live in `lib/routes.tsx` (`APP_ROUTES`), selected
 * here by section key — same arrangement as Energy Advisor, for the same
 * reason (see that module's doc comment).
 *
 * **Hidden routes are owned by Energy Advisor**, not listed here. Matrix
 * Pricing's drilldowns (Capture Health, Capture, Mapping) carry no `section`,
 * and sectionless routes register once globally; they're reached by drilling
 * in from a page that already resolved Operations chrome, so nav context
 * carries them. See `legacyHiddenRoutes` in `energy-advisor-module.tsx`.
 */

/** Entries from `APP_ROUTES` carrying a given legacy section key. */
function legacySection(section: string): AppRoute[] {
  return APP_ROUTES.filter(
    (r) => r.section === section && !r.public && r.status === "live",
  );
}

/**
 * The workspace pages, grouped. `APP_ROUTES` files every one of them under the
 * single legacy `ops-workspace` section key; the grouping is this module's
 * nav concern, so it's decided here by path rather than by re-keying
 * `APP_ROUTES` (whose section keys other surfaces read).
 *
 * Every `ops-workspace` route must land in exactly one group — one left out
 * would silently vanish from the sidebar. `operations-module.test.ts` pins it.
 */
export const OPERATIONS_GROUPS = {
  directory: ["/payees"],
  pricing: ["/dr-peak-shaving", "/matrix-pricing", "/ses-calculators"],
  workflow: ["/cases", "/tasks"],
} as const;

const WORKSPACE_ROUTES = legacySection("ops-workspace");

function workspaceRoutes(paths: ReadonlyArray<string>): AppRoute[] {
  return WORKSPACE_ROUTES.filter((r) => paths.includes(r.path));
}

// Three groups rather than one eight-item "Operations" list — a heading that
// repeated the module's own name and said nothing. Every group sorts A–Z by
// label like the rest of the app; the flat list's `preserveOrder` (which kept
// AB#8433's Cases-below-Tasks and AB#8451's DR-between-Matrix-and-SES) was
// placing items among eight, and doesn't survive the split.
const SECTIONS: ModuleSection[] = [
  {
    // The counterparties ops maintains. Suppliers and Utilities are shared
    // *records* (one declaration each, one canonical url, registered by
    // `recordRoutes()`) pinned into this module's nav rather than the
    // product-wide record bar: only Operations works with either today, so
    // top-level billing overstated their reach. Promote one to `ALL_RECORDS`
    // when a second module needs it — no route or page changes required, just
    // the list it appears in. Not labeled "Records": in the shell that word
    // means the record bar (Accounts, Clients, Deals).
    key: "ops-directory",
    label: "Directory",
    records: ["suppliers", "utilities"],
    routes: workspaceRoutes(OPERATIONS_GROUPS.directory),
  },
  {
    // Where prices are produced or analyzed: the matrix, demand-response and
    // peak-shaving analysis, and the SES calculators.
    key: "ops-pricing",
    label: "Pricing",
    routes: workspaceRoutes(OPERATIONS_GROUPS.pricing),
  },
  {
    // The work queues.
    key: "ops-workflow",
    label: "Workflow",
    routes: workspaceRoutes(OPERATIONS_GROUPS.workflow),
  },
  {
    key: "operations-settings",
    label: "Settings",
    routes: legacySection("operations-settings"),
  },
];

/**
 * The module's settings gear: whether DR & Peak Shaving emails reach internal
 * staff in a lower environment, where every outbound email is otherwise
 * dropped. Declared everywhere but production, whose mail is never guarded;
 * the environment is the hostname's, fixed for the page's life, and the
 * server 404s the setting in production regardless.
 */
const settingsToggles: ModuleSettingsToggle[] =
  detectEnvironment().label === "Prod"
    ? []
    : [
        {
          key: "operations-dr-staff-email",
          element: <StaffEmailToggle area="dr_peak_shaving" label="Enable DR Emails" />,
        },
      ];

export const OPERATIONS_MODULE: ModuleDefinition = {
  key: "operations",
  label: "Operations",
  icon: Wrench,
  // Tasks: the ops work queue, so entering the module opens on what needs
  // doing. Not Matrix Pricing, which it was until AB#8596 — that page was
  // then behind a tenant flag, so with the flag off every entry into
  // Operations bounced to the dashboard. A landing should be gated by
  // permission at most, never by a flag; `moduleLandingPath` covers a caller
  // without `tasks.view` (or `matrix_pricing.view`). Per the
  // deliberate-landing rule in `pilot-web/CLAUDE.md`.
  entryPath: "/tasks",
  requiredPermission: "operations.access",
  // Back-office work runs on Accounts, Clients and Deals, so the record bar
  // stays (AB#8790).
  recordBar: true,
  sections: SECTIONS,
  settingsToggles,
};
